// Client Delivery Wizard (/client-delivery-wizard?program_id=) — sends
// DemandArc-Accepted leads straight to a client's API destination without the
// download-CSV/re-upload round trip through Client Leads Delivery. Reuses
// that page's own connectors/DeliveryJob machinery via the backend's new
// /client-delivery-wizard router — nothing about the CSV flow (LeadDelivery.js,
// LeadDeliverySetup.js) changes.
//
// Standardized on the same shared WizardChrome/WizardStepper this app's other
// core wizard (ProgramWizard.js) uses, including its exact NeTrA theme
// override — see that file for the pattern this one mirrors: one `stepKey`
// state, STEPS computed fresh each render, one step-content component per key.
//
// Reachable only from two entry points (never placed in the nav, same
// precedent as program-new): My Home's "Client delivery" task, and Lead QA
// Review's "⬇ Accepted ▾" menu. Both pass `?program_id=`.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WizardChrome from '../components/WizardChrome';
import WizardStepper from '../components/WizardStepper';
import { programBreakdown } from '../utils/programRoutes';
import { marketoPromptText, marketoAppliedText, failedLeadLines } from '../utils/marketoFeedbackText';

const API = process.env.REACT_APP_API_URL;

// Integrate's async callbacks: how often to re-check a finished send, and how
// long to keep at it before falling back to the manual "Refresh" button.
const FEEDBACK_POLL_INTERVAL_MS = 10000;
const FEEDBACK_POLL_MAX_MS = 10 * 60 * 1000;

// Same override ProgramWizard.js uses — keeps NeTrA's two core wizards
// visually consistent. If a third wizard ever needs this, that's the trigger
// to extract a shared constant, not before (see that file's own comment).
const NETRA_THEME = {
  accent: '#2c3e50',
  link: '#2c3e50',
  connector: '#2c3e50',
  display: 'inherit',
  body: 'inherit',
  stepWidth: '132px',
  connectorWidth: '40px',
  shell: { background: '#ffffff', minHeight: '70vh' },
};

const STEP_LABELS = { setup: 'Setup', review: 'Review', match: 'Match', send: 'Send' };

const card = {
  border: '1px solid #e6e6e6', borderRadius: '8px', padding: '16px',
  marginBottom: '14px', background: '#fbfbfb',
};
const sectionTitle = { margin: '0 0 14px', fontSize: '16px', color: '#2c3e50' };
const bodyPad = { padding: '4px 40px 24px', maxWidth: 'none', width: '100%', margin: '0 auto' };
const smallButton = {
  padding: '6px 12px', background: '#fff', color: '#2c3e50',
  border: '1px solid #ccd1d6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
};
const thStyle = {
  padding: '7px 12px', textAlign: 'left', borderBottom: '1px solid #aed6f1',
  fontWeight: '600', color: '#1a6da8', fontSize: '12px', userSelect: 'none',
  whiteSpace: 'nowrap', background: '#eaf4fb',
};
const tdStyle = {
  padding: '7px 12px', borderBottom: '1px solid #eaf2fb', fontSize: '12px', color: '#333',
};
// Review's column badges (2026-09-11, real ask) — a PM couldn't tell which
// columns actually get delivered to the client (the "client template") apart
// from ones shown purely for NeTrA's own identification/QA purposes (NeTRA
// Lead ID, Flags) — both rendered identically to the real template fields
// before this. Every header now says which bucket it's in explicitly.
const COL_BADGE_BASE = {
  fontSize: '9px', fontWeight: '700', borderRadius: '3px', padding: '1px 5px',
  marginTop: '3px', display: 'inline-block', textTransform: 'uppercase', letterSpacing: '0.3px',
};
const TEMPLATE_BADGE = { ...COL_BADGE_BASE, color: '#1e8449', background: '#d5f5e3' };
const REFERENCE_BADGE = { ...COL_BADGE_BASE, color: '#7f8c8d', background: '#eee' };

function ColHeader({ label, inTemplate }) {
  return (
    <th style={thStyle}>
      <div>{label}</div>
      <div style={inTemplate ? TEMPLATE_BADGE : REFERENCE_BADGE}>
        {inTemplate ? 'Client Template' : 'Reference only'}
      </div>
    </th>
  );
}
const warnBox = {
  background: '#fef9e7', border: '1px solid #f7dc6f', borderRadius: '6px',
  padding: '10px 14px', fontSize: '13px', color: '#7d6608',
};
const errorBox = {
  background: '#fdecea', border: '1px solid #f5c6cb', color: '#a94442',
  borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px',
};

// Same status→color vocabulary LeadDelivery.js's own CSV preflight table uses
// — resolve_lead_field_mapping()'s pairs share that exact vocabulary on
// purpose, so this table renders identically to the "field lights up green"
// experience a PM already knows from the CSV flow.
function pairColor(status) {
  if (status === 'missing') return '#c0392b';
  if (status === 'auto') return '#1a6da8';
  return '#1e8449'; // matched
}
function pairLabel(status) {
  if (status === 'missing') return '✗ missing';
  if (status === 'auto') return '⇄ auto-matched';
  return '✓ mapped';
}

export default function ClientDeliveryWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const programId = useMemo(
    () => new URLSearchParams(location.search).get('program_id'),
    [location.search],
  );

  const [program, setProgram] = useState(null);
  const [stepKey, setStepKey] = useState(null);   // null until setup resolves the initial step
  const [setup, setSetup] = useState(null);
  const [review, setReview] = useState(null);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState({});     // { setup, review, match }
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);

  const [jobs, setJobs] = useState(null);          // [{destination_id, job_id, total, ...live}]
  const [sending, setSending] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  // job_id -> {applying, applied, result:{accepted,failed,skipped}, error} —
  // Marketo's own Sync Leads response is final at push time, so a completed
  // real send against a Marketo destination can immediately prompt the PM to
  // apply that verdict instead of waiting on a separate Check Feedback
  // re-upload (2026-09-11, real ask).
  const [marketoFeedback, setMarketoFeedback] = useState({});
  // job_id -> bool, for the Integrate "🔄 Refresh feedback" button below —
  // separate from `sending` since this fires after a send is already done.
  const [feedbackRefreshing, setFeedbackRefreshing] = useState({});
  // Before this, a PM sending to Integrate had no way to tell from the wizard
  // itself whether the client's async callback had come back or been applied
  // (2026-09-18, real ask after a production send left 5 leads' feedback
  // invisible until someone went and queried the job directly). Integrate's
  // callback arrives after the push completes — ~5-9s for a handful of leads,
  // but a 78-lead send (Elastic, job 90, 2026-09-25) trickled in over ~5
  // minutes — well after the main send-progress polling below has already
  // stopped, so this is a second poll aimed specifically at catching that. It
  // keeps going while any delivered lead is still awaiting its callback, up to
  // FEEDBACK_POLL_MAX_MS. The original version checked only 3 times (3/6/9s)
  // and left the panel stuck on a stale "N of M confirmed" for a big send.
  // `feedbackPollStartRef` marks when this send's poll began, for that cap.
  const feedbackPollStartRef = useRef(null);
  const [feedbackPollExpired, setFeedbackPollExpired] = useState(false);

  const steps = useMemo(() => {
    if (!setup) return [];
    return setup.fully_configured ? ['review', 'match', 'send'] : ['setup', 'review', 'match', 'send'];
  }, [setup]);
  const stepIndex = steps.indexOf(stepKey);

  const setLoad = (key, val) => setLoading(l => ({ ...l, [key]: val }));

  const loadSetup = () => {
    if (!programId) return;
    setLoad('setup', true);
    setError(null);
    axios.get(`${API}/client-delivery-wizard/setup?program_id=${programId}`)
      .then(r => {
        setSetup(r.data);
        setStepKey(prev => prev || (r.data.fully_configured ? 'review' : 'setup'));
      })
      .catch(err => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoad('setup', false));
  };

  useEffect(() => {
    if (!programId) return;
    axios.get(`${API}/programs/${programId}`).then(r => setProgram(r.data)).catch(() => {});
    loadSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  useEffect(() => {
    if (stepKey === 'review' && !review) {
      setLoad('review', true);
      axios.get(`${API}/client-delivery-wizard/review?program_id=${programId}`)
        .then(r => setReview(r.data))
        .catch(err => setError(err.response?.data?.detail || err.message))
        .finally(() => setLoad('review', false));
    }
    if (stepKey === 'match' && !match) {
      setLoad('match', true);
      setError(null);
      axios.get(`${API}/client-delivery-wizard/match?program_id=${programId}`)
        .then(r => setMatch(r.data))
        .catch(err => setError(err.response?.data?.detail || err.message))
        .finally(() => setLoad('match', false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  const toggleEnabled = (enabled) => {
    setToggling(true);
    axios.put(`${API}/programs/${programId}`, { client_delivery_wizard_enabled: enabled })
      .then(() => loadSetup())
      .catch(err => setError(err.response?.data?.detail || err.message))
      .finally(() => setToggling(false));
  };

  const goNext = () => {
    if (stepIndex < 0 || stepIndex >= steps.length - 1) return;
    // Force a fresh Match re-fetch on the way in — leads/mappings may have
    // changed since an earlier visit this session.
    if (steps[stepIndex + 1] === 'match') setMatch(null);
    if (steps[stepIndex + 1] === 'review') setReview(null);
    setStepKey(steps[stepIndex + 1]);
  };
  const goBack = () => {
    if (stepIndex <= 0) { navigate(-1); return; }
    setStepKey(steps[stepIndex - 1]);
  };

  const handleSend = () => {
    setSending(true);
    setError(null);
    feedbackPollStartRef.current = null;
    setFeedbackPollExpired(false);
    setFeedbackRefreshing({});
    axios.post(`${API}/client-delivery-wizard/send?program_id=${programId}&dry_run=${dryRun}`)
      .then(r => setJobs(r.data.jobs.map(j => ({ ...j, status: 'pending', processed: 0, delivered: 0, failed: 0 }))))
      .catch(err => { setError(err.response?.data?.detail || err.message); setSending(false); });
  };

  // Manual "🔄 Refresh feedback" — works any time after a send, not just
  // during the auto-poll window below (Integrate's callback has no documented
  // SLA, so a PM checking back later needs this regardless).
  const handleRefreshFeedback = (jobId) => {
    setFeedbackRefreshing(prev => ({ ...prev, [jobId]: true }));
    axios.get(`${API}/lead-delivery/release-jobs/${jobId}`)
      .then(r => setJobs(prev => prev.map(j => (j.job_id === jobId ? { ...j, ...r.data } : j))))
      .catch(err => setError(err.response?.data?.detail || err.message))
      .finally(() => setFeedbackRefreshing(prev => ({ ...prev, [jobId]: false })));
  };

  const handleApplyMarketoFeedback = (jobId) => {
    setMarketoFeedback(prev => ({ ...prev, [jobId]: { ...prev[jobId], applying: true, error: null } }));
    axios.post(`${API}/client-delivery-wizard/jobs/${jobId}/apply-marketo-feedback`)
      .then(r => setMarketoFeedback(prev => ({ ...prev, [jobId]: { applying: false, applied: true, result: r.data } })))
      .catch(err => setMarketoFeedback(prev => ({
        ...prev, [jobId]: { applying: false, applied: false, error: err.response?.data?.detail || err.message },
      })));
  };

  // Poll every job returned by /send until all reach a terminal state.
  useEffect(() => {
    if (!jobs || !sending) return;
    const interval = setInterval(() => {
      Promise.all(jobs.map(j =>
        axios.get(`${API}/lead-delivery/release-jobs/${j.job_id}`).then(r => r.data).catch(() => null),
      )).then(results => {
        setJobs(prev => prev.map((j, i) => (results[i] ? { ...j, ...results[i] } : j)));
        if (results.every(r => r && (r.status === 'completed' || r.status === 'failed'))) {
          setSending(false);
        }
      });
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs === null, sending]);

  // Follow-up poll once the send itself has finished, aimed only at Integrate
  // destinations' async callback (see feedbackPollStartRef's comment above).
  // Runs every FEEDBACK_POLL_INTERVAL_MS for as long as any delivered lead on a
  // completed real Integrate job still has no callback, then stops once they
  // are all in — or after FEEDBACK_POLL_MAX_MS, after which "🔄 Refresh" is
  // the manual fallback. Only the Integrate jobs are re-fetched.
  const integrateJobIds = useMemo(() => (
    !dryRun && jobs && !sending
      ? jobs.filter(j => j.status === 'completed'
        && match?.groups.find(g => g.destination_id === j.destination_id)?.connector_type === 'integrate')
        .map(j => j.job_id)
      : []
  ), [dryRun, jobs, sending, match]);
  const pendingFeedbackCount = useMemo(() => (
    (jobs || []).filter(j => integrateJobIds.includes(j.job_id))
      .reduce((n, j) => n + (j.results || []).filter(r => r.status === 'Delivered' && !r.callback_received_at).length, 0)
  ), [jobs, integrateJobIds]);
  const feedbackAutoChecking = pendingFeedbackCount > 0 && !feedbackPollExpired;

  useEffect(() => {
    if (!feedbackAutoChecking) return;
    if (!feedbackPollStartRef.current) feedbackPollStartRef.current = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - feedbackPollStartRef.current > FEEDBACK_POLL_MAX_MS) {
        setFeedbackPollExpired(true);
        return;
      }
      Promise.all(integrateJobIds.map(id =>
        axios.get(`${API}/lead-delivery/release-jobs/${id}`).then(r => r.data).catch(() => null),
      )).then(results => {
        setJobs(prev => prev.map(j => {
          const i = integrateJobIds.indexOf(j.job_id);
          return i >= 0 && results[i] ? { ...j, ...results[i] } : j;
        }));
      });
    }, FEEDBACK_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackAutoChecking]);

  const stepperSteps = steps.map((key, i) => ({
    key, label: STEP_LABELS[key],
    state: i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo',
    clickable: i < stepIndex,
  }));

  const matchReady = match ? match.groups.length > 0 && match.groups.every(g => g.ready) : false;
  const reviewReady = review ? review.leads.length > 0 : false;

  const footerFor = () => {
    if (!stepKey) return null;
    if (stepKey === 'setup') {
      return {
        backLabel: '← Back', onBack: goBack,
        continueLabel: 'Continue →', onContinue: goNext,
        continueDisabled: !setup?.fully_configured,
        note: !setup?.fully_configured ? 'Assign a destination to every row below to continue.' : null,
      };
    }
    if (stepKey === 'review') {
      return {
        backLabel: '← Back', onBack: goBack,
        continueLabel: 'Continue →', onContinue: goNext,
        continueDisabled: !reviewReady,
        note: !reviewReady && review ? 'No DemandArc-Accepted leads are waiting to be delivered right now.' : null,
      };
    }
    if (stepKey === 'match') {
      return {
        backLabel: '← Back', onBack: goBack,
        continueLabel: 'Continue →', onContinue: goNext,
        continueDisabled: !matchReady,
        note: !matchReady && match ? 'Resolve every red field before continuing.' : null,
      };
    }
    if (stepKey === 'send') {
      return { backLabel: '← Back', onBack: () => setStepKey('match') };
    }
    return null;
  };

  if (!programId) {
    return (
      <div style={{ padding: '40px' }}>
        <p style={{ color: '#c0392b' }}>No program specified. Open this wizard from a "Client delivery" task or Lead QA Review's Accepted menu.</p>
      </div>
    );
  }

  return (
    <WizardChrome
      title="Client Delivery Wizard"
      backLabel="← Close"
      onBack={() => navigate(-1)}
      chips={program ? [{ label: program.program_name, fg: '#2c3e50', bg: '#eef2f6' }] : []}
      theme={NETRA_THEME}
      footer={footerFor()}
    >
      {steps.length > 0 && (
        <WizardStepper steps={stepperSteps} theme={NETRA_THEME} onStepClick={(k) => setStepKey(k)} />
      )}

      <div style={bodyPad}>
        {error && <div style={errorBox}>{error}</div>}

        {!stepKey && loading.setup && <p style={{ color: '#888' }}>Loading…</p>}

        {stepKey === 'setup' && (
          <SetupStep
            setup={setup} loading={loading.setup} toggling={toggling}
            onToggle={toggleEnabled} onRefresh={loadSetup}
          />
        )}

        {stepKey === 'review' && (
          <ReviewStep review={review} loading={loading.review} />
        )}

        {stepKey === 'match' && (
          <MatchStep match={match} loading={loading.match} />
        )}

        {stepKey === 'send' && (
          <SendStep
            match={match} jobs={jobs} sending={sending} dryRun={dryRun}
            setDryRun={setDryRun} onSend={handleSend}
            marketoFeedback={marketoFeedback} onApplyMarketoFeedback={handleApplyMarketoFeedback}
            feedbackRefreshing={feedbackRefreshing} onRefreshFeedback={handleRefreshFeedback}
            feedbackAutoChecking={feedbackAutoChecking} feedbackPollExpired={feedbackPollExpired}
          />
        )}
      </div>
    </WizardChrome>
  );
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

function SetupStep({ setup, loading, toggling, onToggle, onRefresh }) {
  if (loading && !setup) return <p style={{ color: '#888' }}>Loading…</p>;
  if (!setup) return null;
  return (
    <div>
      <h3 style={sectionTitle}>One-time setup for this program</h3>
      <p style={{ fontSize: '13px', color: '#666', marginTop: '-8px', marginBottom: '16px' }}>
        Every component/goal below needs an active API destination assigned before this
        program can use the wizard — SailPoint-style programs can route different
        components/goals to different destinations; the most specific one always wins.
      </p>
      <div style={card}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!setup.enabled} disabled={toggling}
            onChange={e => onToggle(e.target.checked)} />
          Enable the Client Delivery Wizard for this program
        </label>
      </div>
      <div style={card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Component</th>
              <th style={thStyle}>Goal</th>
              <th style={thStyle}>Destination</th>
            </tr>
          </thead>
          <tbody>
            {setup.groups.map(g => (
              <tr key={g.goal_id}>
                <td style={tdStyle}>{g.component_name}</td>
                <td style={tdStyle}>{g.goal_name}</td>
                <td style={{ ...tdStyle, color: g.resolved_destination_id ? '#1e8449' : '#c0392b' }}>
                  {g.resolved_destination_id ? `✓ ${g.resolved_destination_name}` : '⚠ No destination assigned'}
                </td>
              </tr>
            ))}
            {setup.groups.length === 0 && (
              <tr><td style={tdStyle} colSpan={3}>This program has no goals yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button type="button" style={smallButton}
          onClick={() => window.open('/lead-delivery-setup', '_blank')}>
          ↗ Manage destinations in Delivery Setup
        </button>
        <button type="button" style={smallButton} onClick={onRefresh}>🔄 Refresh</button>
      </div>
    </div>
  );
}

// A table cell for free-text data (QA flags, a client-template "notes"-style
// field, etc.) that can otherwise run many lines and blow out every row's
// height, making the table hard to quick-scan. Clamps to 2 lines and only
// shows a "Show more" toggle when the text actually overflows that — a short
// value never grows a toggle it doesn't need.
function ClampedCell({ text }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  if (!text) return <span style={{ color: '#888' }}>—</span>;

  return (
    <div>
      <div
        ref={ref}
        style={{
          whiteSpace: 'pre-line',
          ...(expanded ? null : {
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {text}
      </div>
      {overflows && (
        <span
          onClick={() => setExpanded(e => !e)}
          style={{ fontSize: '11px', color: '#1a6da8', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          {expanded ? '▲ Show less' : '▼ Show more'}
        </span>
      )}
    </div>
  );
}

function ReviewStep({ review, loading }) {
  if (loading && !review) return <p style={{ color: '#888' }}>Loading…</p>;
  if (!review) return null;
  // Every field the resolved destination(s) actually reference (the "client
  // template") — added alongside the fixed identity columns so a PM can spot
  // bad/blank data before ever reaching Match, not just after. Empty until a
  // destination is configured (Setup incomplete) — the table just falls back
  // to the identity columns alone in that case.
  const templateFields = review.template_fields || [];
  // Which of the fixed identity columns below are ALSO a real client-template
  // field (see get_review()'s identity_fields_in_template) — e.g. "Email"
  // might genuinely be part of the template while "NeTRA Lead ID" never is.
  const identityInTemplate = new Set(review.identity_fields_in_template || []);
  return (
    <div>
      <h3 style={sectionTitle}>Review — {review.leads.length} lead{review.leads.length !== 1 ? 's' : ''} ready to send</h3>
      {review.leads.length === 0 ? (
        <div style={warnBox}>No DemandArc-Accepted leads are waiting to be delivered for this program right now.</div>
      ) : (
        <div style={{ ...card, overflowX: 'auto', padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader label="NeTRA Lead ID" inTemplate={identityInTemplate.has('lead_id')} />
                <ColHeader label="Name" inTemplate={identityInTemplate.has('first_name') || identityInTemplate.has('last_name')} />
                <ColHeader label="Email" inTemplate={identityInTemplate.has('email')} />
                <ColHeader label="Company" inTemplate={identityInTemplate.has('company')} />
                <ColHeader label="Title" inTemplate={identityInTemplate.has('title')} />
                {templateFields.map(f => <ColHeader key={f.key} label={f.label} inTemplate={true} />)}
                <ColHeader label="Flags" inTemplate={false} />
              </tr>
            </thead>
            <tbody>
              {review.leads.map(lead => {
                const hasIssue = (lead.qa_flags || []).length > 0 || Object.keys(lead.unparsed_dates || {}).length > 0;
                return (
                  <tr key={lead.lead_id}>
                    <td style={tdStyle}>{lead.lead_id}</td>
                    <td style={tdStyle}>{[lead.first_name, lead.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td style={tdStyle}>{lead.email || '—'}</td>
                    <td style={tdStyle}>{lead.company || '—'}</td>
                    <td style={tdStyle}>{lead.title || '—'}</td>
                    {templateFields.map(f => (
                      <td key={f.key} style={{ ...tdStyle, maxWidth: '260px' }}>
                        <ClampedCell text={lead[f.key] != null ? String(lead[f.key]) : ''} />
                      </td>
                    ))}
                    <td style={{ ...tdStyle, maxWidth: '260px', color: hasIssue ? '#c0392b' : '#888' }}>
                      <ClampedCell
                        text={[
                          (lead.qa_flags || []).join('; '),
                          ...Object.entries(lead.unparsed_dates || {}).map(
                            ([field, val]) => `could not parse ${field}: "${val}"`
                          ),
                        ].filter(Boolean).join('\n')}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MatchStep({ match, loading }) {
  if (loading && !match) return <p style={{ color: '#888' }}>Loading…</p>;
  if (!match) return null;
  return (
    <div>
      <h3 style={sectionTitle}>Match — field-to-destination preview</h3>
      {match.groups.length === 0 && (
        <div style={warnBox}>No leads resolved to an active destination.</div>
      )}
      {match.groups.map(g => (
        <div key={g.destination_id} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong style={{ fontSize: '13px', color: '#2c3e50' }}>
              {g.destination_name} <span style={{ color: '#888', fontWeight: 400 }}>({g.connector_type})</span>
            </strong>
            <span style={{ fontSize: '12px', color: g.ready ? '#1e8449' : '#c0392b', fontWeight: 600 }}>
              {g.ready ? '✓ Ready' : '✗ Not ready'} — {g.lead_count} lead{g.lead_count !== 1 ? 's' : ''}
            </span>
          </div>
          {g.routing_problems.length > 0 && (
            <div style={{ ...warnBox, background: '#fdecea', borderColor: '#f5c6cb', color: '#a94442', marginBottom: '10px' }}>
              {g.routing_problems.join('; ')}
            </div>
          )}
          {g.program_routing && (
            <div style={{ marginBottom: '12px', border: '1px solid #d6e4f0', borderRadius: '5px', background: '#f6fafd', padding: '10px 12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#2c3e50', marginBottom: '6px' }}>
                Program routing — by {g.program_routing.field}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Title sent to Marketo</th>
                    <th style={thStyle}>Marketo program</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {g.program_routing.groups.map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...tdStyle, color: r.count ? '#2c3e50' : '#aaa' }}>{r.title}</td>
                      <td style={{ ...tdStyle, color: r.count ? '#2c3e50' : '#aaa' }}>{r.program_name}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: r.count ? '#1e8449' : '#aaa', fontWeight: r.count ? 600 : 400 }}>{r.count}</td>
                    </tr>
                  ))}
                  {g.program_routing.unmatched.map((u, i) => (
                    <tr key={`u${i}`}>
                      <td style={{ ...tdStyle, color: '#c0392b' }}>{u.value ? `“${u.value}”` : <em>(blank)</em>}</td>
                      <td style={{ ...tdStyle, color: '#c0392b' }}><em>✗ no program — won&apos;t be sent</em></td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#c0392b', fontWeight: 600 }}>{u.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Destination Field</th>
                <th style={thStyle}>Lead Field</th>
                <th style={thStyle}>Match</th>
              </tr>
            </thead>
            <tbody>
              {g.pairs.map((p, i) => {
                const color = pairColor(p.status);
                const blanks = p.lead_field && g.blank_counts?.[p.lead_field];
                return (
                  <Fragment key={i}>
                    <tr>
                      <td style={{ ...tdStyle, color }}>{p.destination_field}</td>
                      <td style={{ ...tdStyle, color }}>
                        {p.lead_field
                          ? <>{p.lead_field}{blanks ? <span style={{ color: '#b7950b' }}> · {blanks} blank</span> : null}</>
                          : <em>— not found{p.wanted ? ` (expected "${p.wanted}")` : ''}</em>}
                      </td>
                      <td style={{ ...tdStyle, color, whiteSpace: 'nowrap' }}>{pairLabel(p.status)}</td>
                    </tr>
                    {p.mapping_mismatch && (
                      <tr>
                        <td colSpan={3} style={{ ...tdStyle, color: '#b8860b', background: '#fff8e1', fontSize: '12px' }}>
                          ⚠ Your existing CSV mapping for this field ("{p.mapping_mismatch.csv_column}") looks like it
                          points to <strong>{p.mapping_mismatch.csv_implied_field}</strong>, not{' '}
                          <strong>{p.lead_field || '— not found'}</strong> — verify this is still correct before sending.
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {Object.keys(g.fixed_values || {}).length > 0 && (
            <div style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>
              Always sent as-is: {Object.entries(g.fixed_values).map(([k, v]) => `${k}=${v || '(blank)'}`).join(', ')}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SendStep({
  match, jobs, sending, dryRun, setDryRun, onSend, marketoFeedback, onApplyMarketoFeedback,
  feedbackRefreshing, onRefreshFeedback, feedbackAutoChecking, feedbackPollExpired,
}) {
  const totalLeads = match ? match.groups.reduce((n, g) => n + g.lead_count, 0) : 0;
  const allDone = jobs && jobs.every(j => j.status === 'completed' || j.status === 'failed');
  const connectorFor = (destId) => match?.groups.find(g => g.destination_id === destId)?.connector_type;

  return (
    <div>
      <h3 style={sectionTitle}>Send</h3>
      {!jobs && (
        <div style={card}>
          <p style={{ fontSize: '13px', color: '#555' }}>
            Ready to send {totalLeads} lead{totalLeads !== 1 ? 's' : ''}
            {match ? ` across ${match.groups.length} destination${match.groups.length !== 1 ? 's' : ''}` : ''}.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '14px', cursor: 'pointer' }}>
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
            Test mode (dry run) — builds and validates every payload, but nothing is actually sent
          </label>
          <button type="button" onClick={onSend} disabled={sending}
            style={{
              padding: '9px 20px', background: sending ? '#bdc3c7' : '#27ae60', color: '#fff',
              border: 'none', borderRadius: '5px', cursor: sending ? 'default' : 'pointer',
              fontWeight: 600, fontSize: '13px',
            }}>
            {sending ? 'Starting…' : (dryRun ? '▶ Run Test Send' : '▶ Send Now')}
          </button>
        </div>
      )}

      {jobs && jobs.map(j => (
        <div key={j.job_id} style={card}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#2c3e50', marginBottom: '6px' }}>
            Destination #{j.destination_id} — {j.status === 'pending' ? 'Starting…' : `${j.status}`}
          </div>
          {(j.status === 'pending' || j.status === 'running') && (
            <div style={{ fontSize: '12px', color: '#555' }}>
              Processing {j.processed || 0} of {j.total}
              {!dryRun ? ` — ${j.delivered || 0} delivered, ${j.failed || 0} failed` : ''}.
              <div style={{ marginTop: '4px', height: '6px', background: '#eee', borderRadius: '3px', overflow: 'hidden', maxWidth: '360px' }}>
                <div style={{
                  height: '100%', background: dryRun ? '#1a6da8' : '#27ae60', borderRadius: '3px',
                  width: `${j.total ? Math.round(((j.processed || 0) / j.total) * 100) : 0}%`,
                  transition: 'width 0.4s ease',
                }} />
              </div>
            </div>
          )}
          {j.status === 'completed' && (
            <div style={{ fontSize: '12px', color: '#555' }}>
              {dryRun
                ? `Simulated ${(j.results || []).filter(r => r.status === 'Simulated').length} of ${j.total} lead${j.total !== 1 ? 's' : ''} — no data was sent.`
                : `${j.delivered || 0} delivered, ${j.failed || 0} failed.`}
              {programBreakdown(j.results).length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '2px' }}>
                    {dryRun ? 'Would go to these Marketo programs:' : 'Sent to these Marketo programs:'}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px' }}>
                    {programBreakdown(j.results).map(b => (
                      <li key={b.program}>{b.program} — {b.count}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(j.results || []).some(r => r.status === 'Failed') && (
                <ul style={{ marginTop: '8px', paddingLeft: '18px' }}>
                  {j.results.filter(r => r.status === 'Failed').map(r => (
                    <li key={r.lead_id} style={{ color: '#c0392b' }}>{r.netra_lead_id || r.lead_id}: {r.error}</li>
                  ))}
                </ul>
              )}
              {/* Marketo's Sync Leads response is final at push time (created/
                  updated, or a skip reason) — no need to make the PM wait on a
                  separate Check Feedback re-upload the way Convertr/Integrate's
                  async flows do. Real ask, 2026-09-11. */}
              {!dryRun && connectorFor(j.destination_id) === 'marketo' && (
                <div style={{ marginTop: '10px', padding: '10px 12px', background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '5px' }}>
                  {marketoFeedback[j.job_id]?.applied ? (
                    <div>
                      <div style={{ color: '#1e8449', fontWeight: 600 }}>{marketoAppliedText(marketoFeedback[j.job_id].result)}</div>
                      {failedLeadLines(marketoFeedback[j.job_id].result).length > 0 && (
                        <ul style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: '11px', color: '#7e5109' }}>
                          {failedLeadLines(marketoFeedback[j.job_id].result).map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: '8px' }}>{marketoPromptText(j.delivered, j.failed)}</div>
                      {marketoFeedback[j.job_id]?.error && (
                        <div style={{ color: '#c0392b', marginBottom: '8px' }}>{marketoFeedback[j.job_id].error}</div>
                      )}
                      <button type="button" onClick={() => onApplyMarketoFeedback(j.job_id)} disabled={marketoFeedback[j.job_id]?.applying}
                        style={{
                          padding: '6px 14px', background: marketoFeedback[j.job_id]?.applying ? '#bdc3c7' : '#1a6da8', color: '#fff',
                          border: 'none', borderRadius: '4px', cursor: marketoFeedback[j.job_id]?.applying ? 'default' : 'pointer',
                          fontSize: '12px', fontWeight: 600,
                        }}>
                        {marketoFeedback[j.job_id]?.applying ? 'Updating…' : 'Update Lead Status'}
                      </button>
                    </>
                  )}
                </div>
              )}
              {/* Integrate's callback is genuinely async and one-shot — before
                  this, a PM had no way to tell from the wizard whether a
                  callback had come back at all, let alone whether it was
                  applied (2026-09-18, real ask following a production send
                  where exactly that happened). Shows per-lead where each one
                  stands; "🔄 Refresh" re-fetches on demand since Integrate
                  documents no SLA for when the callback arrives. */}
              {!dryRun && connectorFor(j.destination_id) === 'integrate' && (
                <IntegrateFeedbackPanel
                  job={j} refreshing={!!feedbackRefreshing?.[j.job_id]}
                  onRefresh={() => onRefreshFeedback(j.job_id)}
                  autoChecking={feedbackAutoChecking} autoStopped={feedbackPollExpired}
                />
              )}
            </div>
          )}
          {j.status === 'failed' && (
            <div style={{ color: '#c0392b', fontSize: '12px' }}>{j.error || 'The job failed before any leads were processed.'}</div>
          )}
        </div>
      ))}

      {allDone && (
        <p style={{ fontSize: '12px', color: '#888' }}>
          {dryRun
            ? 'This was a test send — nothing was actually delivered. Uncheck "Test mode" and send again to deliver for real.'
            : 'Delivered leads are stamped as sent and will drop off "Client delivery" tasks automatically.'}
        </p>
      )}
    </div>
  );
}

// One row per delivered lead's client-feedback status. Reads the same
// `callback_received_at`/`callback_verdict` fields the backend's job.results
// already carries (app/routers/client_delivery_wizard.py's integrate_callback
// endpoint writes them) — no new endpoint, this just renders what the
// existing job-polling fetch was already bringing back unread.
function feedbackLabel(result) {
  if (!result.callback_received_at) {
    return <span style={{ color: '#888' }}>⏳ Awaiting client feedback</span>;
  }
  if (result.callback_verdict === 'accepted') {
    return <span style={{ color: '#1e8449', fontWeight: 600 }}>✓ Accepted by client</span>;
  }
  if (result.callback_verdict === 'rejected') {
    return <span style={{ color: '#c0392b', fontWeight: 600 }}>✗ Rejected by client</span>;
  }
  // "unclassified" — a callback genuinely arrived but didn't match any known
  // shape. Surfaced plainly rather than hidden, since this is exactly the
  // state that otherwise goes unnoticed (see this block's own origin story).
  return <span style={{ color: '#b9770e', fontWeight: 600 }}>⚠ Response received, unrecognized format — needs manual review</span>;
}

function IntegrateFeedbackPanel({ job, refreshing, onRefresh, autoChecking, autoStopped }) {
  const delivered = (job.results || []).filter(r => r.status === 'Delivered');
  const confirmed = delivered.filter(r => !!r.callback_received_at).length;
  const waiting = delivered.length - confirmed;
  return (
    <div style={{ marginTop: '10px', padding: '10px 12px', background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '5px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: delivered.length ? '8px' : 0 }}>
        <div>
          <div style={{ fontWeight: 600, color: '#2c3e50' }}>
            Client feedback (async) — {confirmed} of {delivered.length} lead{delivered.length !== 1 ? 's' : ''} confirmed
          </div>
          {waiting > 0 && autoChecking && (
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
              Still waiting on {waiting} — checking automatically every {FEEDBACK_POLL_INTERVAL_MS / 1000} seconds.
            </div>
          )}
          {waiting > 0 && autoStopped && (
            <div style={{ fontSize: '11px', color: '#b9770e', marginTop: '2px' }}>
              Automatic checking stopped after {FEEDBACK_POLL_MAX_MS / 60000} minutes — click Refresh to check again.
            </div>
          )}
        </div>
        <button type="button" onClick={onRefresh} disabled={refreshing}
          style={{
            padding: '3px 10px', background: '#fff', color: '#1a6da8', border: '1px solid #aed6f1',
            borderRadius: '4px', cursor: refreshing ? 'default' : 'pointer', fontSize: '11px', fontWeight: 600,
          }}>
          {refreshing ? 'Refreshing…' : '🔄 Refresh'}
        </button>
      </div>
      {delivered.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px' }}>
          {delivered.map(r => (
            <li key={r.lead_id} style={{ marginBottom: '3px' }}>
              {r.netra_lead_id || r.lead_id}: {feedbackLabel(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
